/**
 * Composant Calendar avancé pour le plugin Calendar modulaire
 * Gestion avancée des vues, récurrences, rappels et interactions
 */

import React, { useState, useMemo, useEffect } from 'react';
import { CalendarFormatters } from '../utils/CalendarUtils';

// Interafaces locales
export interface CalendarEvent {
  id: string;
  title: string;
  category: 'work' | 'personal' | 'meeting' | 'reminder' | 'holiday' | 'other';
  date: string;
  endDate?: string;
  time?: string;
  endTime?: string;
  description?: string;
  color?: string;
  location?: string;
  attendees?: string[];
  recurrence?: 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';
  reminder?: number;
  allDay?: boolean;
  priority?: 'low' | 'normal' | 'high';
}

export interface CalendarConfig {
  id?: string;
  title?: string;
  description?: string;
  events?: CalendarEvent[];
  viewMode?: 'month' | 'week' | 'day' | 'agenda';
  theme?: 'default' | 'minimal' | 'colorful' | 'dark' | 'business' | 'light';
  
  defaultDate?: string;
  showWeekends?: boolean;
  showEventDetails?: boolean;
  allowEventCreation?: boolean;
  timeFormat?: '12h' | '24h';
  firstDayOfWeek?: 0 | 1;
  
  // Style
  backgroundColor?: string;
  textColor?: string;
  headerTextColor?: string;
  accentColor?: string;
  calendarHeight?: number;

  defaultView?: string; 
  language?: string;
}

interface AdvancedCalendarProps {
  config: CalendarConfig;
  isPreview?: boolean;
  // ✅ AJOUT : Callback pour notifier la création
  onEventAdd?: (event: CalendarEvent) => void;
  onEventUpdate?: (event: CalendarEvent) => void;
}

export const AdvancedCalendar: React.FC<AdvancedCalendarProps> = ({ config, onEventAdd, onEventUpdate }) => {
  // Initialisation de la date
  const [currentDate, setCurrentDate] = useState(() => {
    return config.defaultDate ? new Date(config.defaultDate) : new Date();
  });

  const [localEvents, setLocalEvents] = useState<CalendarEvent[]>(config.events || []);
  
  useEffect(() => {
    if (config.events) {
        setLocalEvents(prevEvents => {
            const visitorEvents = prevEvents.filter(e => e.id.startsWith('visitor_'));
            const configEvents = config.events || [];
            const configEventIds = new Set(configEvents.map(e => e.id));
            const uniqueVisitorEvents = visitorEvents.filter(e => !configEventIds.has(e.id));
            return [...configEvents, ...uniqueVisitorEvents];
        });
    }
  }, [config.events]);

  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day' | 'agenda'>(config.viewMode || 'month');

  useEffect(() => {
      if (config.viewMode) setViewMode(config.viewMode);
  }, [config.viewMode]);

  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [showEventDetails, setShowEventDetails] = useState(false);
  
  // États pour la création d'événement (Nouveau)
  const [isCreating, setIsCreating] = useState(false);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [editingBaseEvent, setEditingBaseEvent] = useState<CalendarEvent | null>(null);
  const [creationData, setCreationData] = useState<{date: string; endDate: string; title: string; priority: 'low'|'normal'|'high'; category: CalendarEvent['category']}>({
      date: '',
      endDate: '',
      title: '',
      priority: 'normal',
      category: 'personal'
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredEvents = useMemo(() => {
    let events = localEvents;
    if (searchTerm) {
      events = events.filter(event => 
        event.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return events;
  }, [localEvents, searchTerm]);

  // ✅ Gestionnaire de clic date mis à jour (Ouvre la modale au lieu du prompt)
  const handleDateClick = (date: Date) => {
    if (!config.allowEventCreation) return;
    
    setEditingEventId(null);
    setEditingBaseEvent(null);
    setCreationData({
        date: date.toISOString().split('T')[0],
        endDate: '',
        title: '',
        priority: 'normal',
        category: 'personal'
    });
    setIsCreating(true);
  };

  const handleEventClick = (evt: CalendarEvent) => {
    if (onEventUpdate) {
        setEditingEventId(evt.id);
        setEditingBaseEvent(evt);
        setCreationData({
            date: evt.date,
            endDate: evt.endDate || '',
            title: evt.title,
            priority: evt.priority || 'normal',
            category: evt.category || 'personal'
        });
        setIsCreating(true);
        setShowEventDetails(false);
        return;
    }

    setSelectedEvent(evt);
    setShowEventDetails(true);
  };

  // ✅ Sauvegarde du nouvel événement
  const handleSaveEvent = () => {
    if (!creationData.title.trim()) return;

    const normalizedEndDate = creationData.endDate && creationData.endDate >= creationData.date
      ? creationData.endDate
      : '';
    const hasRange = Boolean(normalizedEndDate && normalizedEndDate !== creationData.date);

    if (editingEventId) {
        const baseEvent = editingBaseEvent || localEvents.find(event => event.id === editingEventId);
        const updatedEvent: CalendarEvent = {
            ...(baseEvent || {
              id: editingEventId,
              category: creationData.category,
              date: creationData.date,
              title: creationData.title,
              priority: creationData.priority
            }),
            title: creationData.title,
            date: creationData.date,
            category: creationData.category,
            priority: creationData.priority
        };

        if (hasRange) {
            updatedEvent.endDate = normalizedEndDate;
        } else {
            delete updatedEvent.endDate;
        }

        if (onEventUpdate) {
            onEventUpdate(updatedEvent);
        } else {
            setLocalEvents(prev => prev.map(event => event.id === updatedEvent.id ? updatedEvent : event));
        }

        setIsCreating(false);
        setEditingEventId(null);
        setEditingBaseEvent(null);
        return;
    }

    const newEvent: CalendarEvent = {
        id: `visitor_${Date.now()}`,
        title: creationData.title,
        date: creationData.date,
        ...(hasRange ? { endDate: normalizedEndDate } : {}),
        category: creationData.category, 
        priority: creationData.priority
    };
    
    // ✅ LOGIQUE DE SAUVEGARDE AMÉLIORÉE
    if (onEventAdd) {
        // 1. Si un callback est fourni (ex: Editor), on l'utilise pour persister dans la config globale
        onEventAdd(newEvent);
    } else {
        // 2. Sinon (ex: Runtime simple), on met à jour l'affichage local uniquement
        setLocalEvents(prev => [...prev, newEvent]);
    }
    
    setIsCreating(false);
    setEditingEventId(null);
    setEditingBaseEvent(null);
  };

  const closeEventModal = () => {
    setIsCreating(false);
    setEditingEventId(null);
    setEditingBaseEvent(null);
  };

  const getThemeStyles = () => {
    const isTransparent = config.backgroundColor === 'transparent';
    const baseStyles = {
      backgroundColor: config.backgroundColor || '#ffffff',
      color: config.textColor || '#333333',
      borderRadius: '12px',
      border: isTransparent ? 'none' : '1px solid #dee2e6',
      overflow: 'hidden',
      boxShadow: isTransparent ? 'none' : '0 4px 6px rgba(0, 0, 0, 0.1)'
    };
    if (config.theme === 'minimal') return { ...baseStyles, border: 'none', boxShadow: 'none' };
    return baseStyles;
  };

  const getEventDateRange = (event: CalendarEvent) => {
    const start = event.date;
    const end = event.endDate && event.endDate >= start ? event.endDate : start;
    return { start, end };
  };

  const formatEventDateRange = (event: CalendarEvent) => {
    const { start, end } = getEventDateRange(event);
    const startLabel = new Date(start).toLocaleDateString();
    if (start === end) return startLabel;
    const endLabel = new Date(end).toLocaleDateString();
    return `${startLabel} → ${endLabel}`;
  };

  const getEventsForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return filteredEvents.filter(event => {
      const { start, end } = getEventDateRange(event);
      return dateStr >= start && dateStr <= end;
    });
  };

  const navigateDate = (direction: number) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') newDate.setDate(newDate.getDate() + direction);
    else if (viewMode === 'week') newDate.setDate(newDate.getDate() + (direction * 7));
    else newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  // ✅ Helper pour la couleur de priorité (Soulignement)
  const getPriorityColor = (priority?: string) => {
      switch(priority) {
          case 'high': return '#dc3545'; // Rouge
          case 'low': return '#0d6efd';  // Bleu
          default: return '#198754';     // Vert (Normal)
      }
  };

  // --- RENDERERS ---

  // ✅ Rendu Badge mis à jour (Soulignement)
  const renderEventBadge = (evt: CalendarEvent) => (
      <div key={evt.id} style={{
          fontSize: '11px',
          backgroundColor: 'transparent',
          color: config.textColor || '#333',
          padding: '1px 4px',
          marginBottom: '2px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          cursor: 'pointer',
          // Style trait souligné
          borderBottom: `2px solid ${getPriorityColor(evt.priority)}`,
          fontWeight: '500'
      }}
      onClick={(e) => {
          e.stopPropagation();
          handleEventClick(evt);
      }}>
          {evt.title}
      </div>
  );

  const renderMonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayIndex = (firstDay.getDay() - (config.firstDayOfWeek || 1) + 7) % 7;
    
    const weeks = [];
    let currentWeek = [];

    for (let i = 0; i < startDayIndex; i++) {
        currentWeek.push(<div key={`empty-start-${i}`} className="calendar-day empty" style={{ backgroundColor: config.backgroundColor === 'transparent' ? 'rgba(255,255,255,0.3)' : '#f9f9f9', opacity: 0.5 }} />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        const dayEvents = getEventsForDate(date);
        const isToday = new Date().toDateString() === date.toDateString();
        
        currentWeek.push(
            <div 
                key={day} 
                onClick={() => handleDateClick(date)}
                style={{
                    minHeight: '100px',
                    borderTop: '1px solid #eee',
                    borderLeft: '1px solid #eee',
                    padding: '4px',
                    cursor: config.allowEventCreation ? 'pointer' : 'default',
                    backgroundColor: isToday ? `${config.accentColor}10` : 'transparent',
                    position: 'relative'
                }}
            >
                <div style={{ fontWeight: isToday ? 'bold' : 'normal', marginBottom: '4px', fontSize: '14px' }}>
                    {day}
                </div>
                {dayEvents.map(evt => renderEventBadge(evt))}
            </div>
        );

        if (currentWeek.length === 7) {
            weeks.push(<div key={`week-${day}`} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>{currentWeek}</div>);
            currentWeek = [];
        }
    }

    if (currentWeek.length > 0) {
        while (currentWeek.length < 7) {
            currentWeek.push(<div key={`empty-end-${currentWeek.length}`} className="calendar-day empty" style={{ backgroundColor: config.backgroundColor === 'transparent' ? 'rgba(255,255,255,0.3)' : '#f9f9f9', opacity: 0.5 }} />);
        }
        weeks.push(<div key="week-last" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>{currentWeek}</div>);
    }

    const daysHeader = (config.firstDayOfWeek === 1 ? ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'] : ['Dim','Lun','Mar','Mer','Jeu','Ven','Sam']);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', backgroundColor: config.backgroundColor === 'transparent' ? 'rgba(0,0,0,0.05)' : '#f1f1f1', fontWeight: 'bold', fontSize: '13px', textAlign: 'center', padding: '8px 0', borderBottom: '1px solid #ddd' }}>
                {daysHeader.map(d => <div key={d}>{d}</div>)}
            </div>
            {weeks}
        </div>
    );
  };

  const renderWeekView = () => {
    const curr = new Date(currentDate);
    const day = curr.getDay();
    const diff = curr.getDate() - day + (day === 0 ? -6 : 1);
    
    const weekStart = new Date(curr.setDate(diff));
    const weekDays = [];
    
    for (let i = 0; i < 7; i++) {
        const dayDate = new Date(weekStart);
        dayDate.setDate(weekStart.getDate() + i);
        
        const dayEvents = getEventsForDate(dayDate);
        const isToday = new Date().toDateString() === dayDate.toDateString();
        const dateStr = dayDate.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' });

        weekDays.push(
            <div key={i} style={{ flex: 1, minWidth: '100px', borderLeft: i > 0 ? '1px solid #eee' : 'none', display: 'flex', flexDirection: 'column' }}>
                <div style={{ 
                    padding: '8px', 
                    textAlign: 'center', 
                    backgroundColor: isToday ? config.accentColor : '#f8f9fa',
                    color: isToday ? '#fff' : '#333',
                    borderBottom: '1px solid #ddd',
                    fontWeight: 'bold',
                    fontSize: '13px'
                }}>
                    {dateStr}
                </div>
                <div 
                    style={{ flex: 1, padding: '4px', minHeight: '300px', cursor: config.allowEventCreation ? 'pointer' : 'default' }}
                    onClick={() => handleDateClick(dayDate)}
                >
                    {dayEvents.map(evt => renderEventBadge(evt))}
                </div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', border: '1px solid #eee', overflowX: 'auto' }}>
            {weekDays}
        </div>
    );
  };

  // Agenda simplifié
  const renderAgendaView = () => (
      <div style={{ padding: '15px' }}>
          <h5>Agenda</h5>
          {filteredEvents.length === 0 && <p className="text-muted">Aucun événement.</p>}
          {filteredEvents.map(evt => (
              <div key={evt.id} style={{ padding: '10px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                      <strong>{formatEventDateRange(evt)}</strong> - {evt.title}
                  </div>
                  <div style={{ 
                      width: '12px', height: '12px', borderRadius: '50%', 
                      backgroundColor: getPriorityColor(evt.priority) 
                  }} title={`Priorité ${evt.priority || 'normal'}`} />
              </div>
          ))}
      </div>
  );

  return (
    <div style={{ 
        ...getThemeStyles(), 
        width: '100%', 
        minHeight: '400px',
        height: 'auto',
        display: 'flex', 
        flexDirection: 'column' 
    }}>
      {/* ✅ TITRE AJOUTÉ */}
      {config.title && (
          <div style={{ padding: '10px 15px 0', fontWeight: 'bold', fontSize: '1.25rem' }}>
              {config.title}
          </div>
      )}

      {/* HEADER : Navigation + Changement vues */}
      <div style={{ 
          padding: '10px 15px', 
          backgroundColor: 'transparent',
          color: config.accentColor || '#0d6efd',
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px',
          borderBottom: '1px solid #eee'
      }}>
          <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
              <button onClick={() => navigateDate(-1)} className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 10px' }}>&lt;</button>
              
              <span style={{ fontWeight: 'bold', minWidth: '140px', textAlign: 'center', color: config.textColor || '#333' }}>
                  {viewMode === 'day' ? currentDate.toLocaleDateString() : 
                   viewMode === 'week' ? `Semaine du ${currentDate.toLocaleDateString()}` :
                   currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
              </span>
              
              <button onClick={() => navigateDate(1)} className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 10px' }}>&gt;</button>
          </div>

          <div className="btn-group btn-group-sm">
            <button className={`btn ${viewMode === 'month' ? 'btn-primary' : 'btn-outline-primary'}`} style={{backgroundColor: viewMode === 'month' ? config.accentColor : 'transparent', color: viewMode === 'month' ? '#fff' : config.accentColor, borderColor: config.accentColor}} onClick={() => setViewMode('month')}>Mois</button>
            <button className={`btn ${viewMode === 'week' ? 'btn-primary' : 'btn-outline-primary'}`} style={{backgroundColor: viewMode === 'week' ? config.accentColor : 'transparent', color: viewMode === 'week' ? '#fff' : config.accentColor, borderColor: config.accentColor}} onClick={() => setViewMode('week')}>Semaine</button>
            <button className={`btn ${viewMode === 'agenda' ? 'btn-primary' : 'btn-outline-primary'}`} style={{backgroundColor: viewMode === 'agenda' ? config.accentColor : 'transparent', color: viewMode === 'agenda' ? '#fff' : config.accentColor, borderColor: config.accentColor}} onClick={() => setViewMode('agenda')}>Agenda</button>
          </div>
      </div>

      {/* BODY */}
      <div style={{ flex: 1, backgroundColor: config.backgroundColor === 'transparent' ? 'transparent' : (config.backgroundColor || '#fff') }}>
          {viewMode === 'month' && renderMonthView()}
          {viewMode === 'week' && renderWeekView()}
          {viewMode === 'agenda' && renderAgendaView()}
      </div>

      {/* MODAL DÉTAILS ÉVÉNEMENT */}
      {showEventDetails && selectedEvent && (
          <div style={{ position: 'fixed', top:0,left:0,right:0,bottom:0, background:'rgba(0,0,0,0.5)', zIndex:999, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <div style={{ background: '#fff', padding: '20px', borderRadius: '8px', minWidth: '300px', maxWidth: '400px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                  <h5 style={{ borderBottom: `3px solid ${getPriorityColor(selectedEvent.priority)}`, paddingBottom: '8px', display: 'inline-block' }}>
                      {selectedEvent.title}
                  </h5>
                  <div className="mt-3">
                      <p><strong>Dates :</strong> {formatEventDateRange(selectedEvent)}</p>
                      <p><strong>Priorité :</strong> {selectedEvent.priority === 'high' ? 'Haute 🔴' : selectedEvent.priority === 'low' ? 'Basse 🔵' : 'Normale 🟢'}</p>
                      {selectedEvent.description && <p>{selectedEvent.description}</p>}
                  </div>
                  <div className="mt-4 text-end">
                      <button className="btn btn-sm btn-secondary" onClick={() => setShowEventDetails(false)}>Fermer</button>
                  </div>
              </div>
          </div>
      )}

      {/* ✅ MODAL CRÉATION ÉVÉNEMENT (VISITEUR) */}
      {isCreating && (
          <div style={{ position: 'fixed', top:0,left:0,right:0,bottom:0, background:'rgba(0,0,0,0.5)', zIndex:999, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <div style={{ background: '#fff', padding: '20px', borderRadius: '8px', minWidth: '320px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                  <h6 className="mb-3">
                      {editingEventId ? 'Modifier l’événement' : 'Nouvel événement'} {creationData.date ? `(${new Date(creationData.date).toLocaleDateString()})` : ''}
                  </h6>
                  
                  <div className="mb-3">
                      <label className="form-label small fw-bold">Titre</label>
                      <input 
                          type="text" 
                          className="form-control" 
                          autoFocus
                          value={creationData.title}
                          onChange={(e) => setCreationData({...creationData, title: e.target.value})}
                          placeholder="Ex: Réunion projet..."
                      />
                  </div>

                  <div className="mb-3">
                      <label className="form-label small fw-bold">Date de début</label>
                      <input 
                          type="date"
                          className="form-control"
                          value={creationData.date}
                          onChange={(e) => setCreationData(prev => ({
                            ...prev,
                            date: e.target.value,
                            endDate: prev.endDate && prev.endDate < e.target.value ? e.target.value : prev.endDate
                          }))}
                      />
                  </div>

                  <div className="mb-4">
                      <label className="form-label small fw-bold">Date de fin (optionnel)</label>
                      <input 
                          type="date"
                          className="form-control"
                          min={creationData.date}
                          value={creationData.endDate}
                          onChange={(e) => setCreationData(prev => ({
                            ...prev,
                            endDate: e.target.value
                          }))}
                      />
                      <small className="text-muted d-block mt-1" style={{fontSize: '0.75rem'}}>
                          Laissez vide pour un événement sur une seule journée.
                      </small>
                  </div>

                  <div className="mb-3">
                      <label className="form-label small fw-bold">Catégorie</label>
                      <select
                          className="form-select"
                          value={creationData.category}
                          onChange={(e) => setCreationData(prev => ({
                            ...prev,
                            category: e.target.value as CalendarEvent['category']
                          }))}
                      >
                          <option value="work">💼 Pro</option>
                          <option value="meeting">🤝 Réunion</option>
                          <option value="personal">🏠 Perso</option>
                          <option value="holiday">🏖️ Vacances</option>
                          <option value="reminder">⏰ Rappel</option>
                          <option value="other">📋 Autre</option>
                      </select>
                  </div>

                  <div className="mb-4">
                      <label className="form-label small fw-bold">Priorité</label>
                      <div className="btn-group w-100" role="group">
                          <input type="radio" className="btn-check" name="priority" id="prio-low" autoComplete="off" 
                              checked={creationData.priority === 'low'} onChange={() => setCreationData({...creationData, priority: 'low'})} />
                          <label className="btn btn-outline-primary" htmlFor="prio-low">Bas 🔵</label>

                          <input type="radio" className="btn-check" name="priority" id="prio-normal" autoComplete="off" 
                              checked={creationData.priority === 'normal'} onChange={() => setCreationData({...creationData, priority: 'normal'})} />
                          <label className="btn btn-outline-success" htmlFor="prio-normal">Normal 🟢</label>

                          <input type="radio" className="btn-check" name="priority" id="prio-high" autoComplete="off" 
                              checked={creationData.priority === 'high'} onChange={() => setCreationData({...creationData, priority: 'high'})} />
                          <label className="btn btn-outline-danger" htmlFor="prio-high">Haut 🔴</label>
                      </div>
                      <small className="text-muted d-block mt-1 text-center" style={{fontSize: '0.75rem'}}>
                          {creationData.priority === 'low' && "Souligné en bleu"}
                          {creationData.priority === 'normal' && "Souligné en vert"}
                          {creationData.priority === 'high' && "Souligné en rouge"}
                      </small>
                  </div>

                  <div className="d-flex justify-content-end gap-2">
                      <button className="btn btn-sm btn-light" onClick={closeEventModal}>Annuler</button>
                      <button className="btn btn-sm btn-primary" onClick={handleSaveEvent} disabled={!creationData.title.trim()}>Enregistrer</button>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};

export default AdvancedCalendar;
